import { useModalLifecycle } from '../shared/useModalLifecycle'
import React, { useState } from 'react'
import { X, Video, Plus, Clock, Trash2, CheckCircle2 } from 'lucide-react'
import { VideoSegment } from '../../types/fieldCapture'

interface VideoReferenceModalProps {
  isOpen: boolean
  onClose: () => void
  segments: VideoSegment[]
  onUpdateSegments: (segments: VideoSegment[]) => void
  currentSnapSequence: number
  currentSnapStartedAt: string
  blockStartedAt: string
  activeSegmentId?: string | null
  currentVideoMark?: string | null
  onSetSnapVideoMark: (segmentId: string, mark: string, offsetSec: number) => void
}

export const VideoReferenceModal: React.FC<VideoReferenceModalProps> = ({
  isOpen,
  onClose,
  segments = [],
  onUpdateSegments,
  currentSnapSequence,
  currentSnapStartedAt,
  blockStartedAt,
  activeSegmentId,
  currentVideoMark,
  onSetSnapVideoMark,
}) => {
  const [selectedSegId, setSelectedSegId] = useState<string>(
    activeSegmentId || (segments && segments.length > 0 ? segments[0].id : '')
  )
  const [manualMark, setManualMark] = useState<string>(currentVideoMark || '')
  const [showAddSegment, setShowAddSegment] = useState(false)
  const [newSegName, setNewSegName] = useState('')
  const [newSegDriveUrl, setNewSegDriveUrl] = useState('')
  const [newSegFileName, setNewSegFileName] = useState('')
  const [newSegOffset, setNewSegOffset] = useState<number>(0)

  useModalLifecycle(isOpen, onClose)

  if (!isOpen) return null

  // Calcular tiempo transcurrido desde inicio del bloque hasta el inicio del snap
  const blockStartMs = new Date(blockStartedAt).getTime()
  const snapStartMs = new Date(currentSnapStartedAt).getTime()
  const elapsedSeconds = Math.max(0, Math.floor((snapStartMs - blockStartMs) / 1000))
  const defaultMinutes = Math.floor(elapsedSeconds / 60)
  const defaultSeconds = elapsedSeconds % 60
  const formattedDefaultMark = `${defaultMinutes.toString().padStart(2, '0')}:${defaultSeconds
    .toString()
    .padStart(2, '0')}`

  const handleApplyMark = () => {
    const markToUse = manualMark.trim() || formattedDefaultMark
    const seg = segments.find((s) => s.id === selectedSegId)
    const offset = seg ? seg.offset_seconds : 0
    onSetSnapVideoMark(selectedSegId, markToUse, offset)
    onClose()
  }

  const handleAddSegment = () => {
    if (!newSegName.trim()) return
    const newSeg: VideoSegment = {
      id: crypto.randomUUID(),
      name: newSegName.trim(),
      drive_url: newSegDriveUrl.trim() || null,
      file_name: newSegFileName.trim() || null,
      started_at: new Date().toISOString(),
      offset_seconds: Number(newSegOffset) || 0,
    }
    const updated = [...segments, newSeg]
    onUpdateSegments(updated)
    setSelectedSegId(newSeg.id)
    setShowAddSegment(false)
    setNewSegName('')
    setNewSegDriveUrl('')
    setNewSegFileName('')
    setNewSegOffset(0)
  }

  const handleDeleteSegment = (id: string) => {
    const updated = segments.filter((s) => s.id !== id)
    onUpdateSegments(updated)
    if (selectedSegId === id) {
      setSelectedSegId(updated.length > 0 ? updated[0].id : '')
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(10, 19, 32, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '1rem',
        backdropFilter: 'blur(2px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '520px',
          backgroundColor: 'var(--surface-card)',
          borderRadius: '12px',
          padding: '1.25rem',
          boxShadow: 'var(--shadow-card)',
          maxHeight: '90vh',
          overflowY: 'auto',
          border: '1px solid var(--surface-border)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Cabecera */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem',
            borderBottom: '1px solid var(--surface-border)',
            paddingBottom: '0.75rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '6px',
                backgroundColor: 'var(--nav-bg-surface)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
              }}
            >
              <Video size={18} />
            </div>
            <div>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
                Referencias Temporales y Vídeo
              </h3>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                Vincular Snap #{currentSnapSequence} para cruce manual con cámara
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '0.4rem',
            }}
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Marca de tiempo sugerida */}
        <div
          style={{
            backgroundColor: 'var(--surface-subtle)',
            padding: '0.85rem',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--surface-border)',
            marginBottom: '1rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.76rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
              INSTANTE DEL SNAP #{currentSnapSequence}
            </span>
            <span
              style={{
                fontSize: '0.72rem',
                color: 'var(--text-muted)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              Hora real: {new Date(currentSnapStartedAt).toLocaleTimeString()}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
            <div style={{ flex: 1 }}>
              <label className="form-label" style={{ fontSize: '0.72rem', marginBottom: '0.2rem' }}>
                Minuto/Segundo en vídeo (MM:SS)
              </label>
              <input
                type="text"
                placeholder={formattedDefaultMark}
                value={manualMark}
                onChange={(e) => setManualMark(e.target.value)}
                className="form-input"
                style={{
                  fontFamily: 'monospace',
                  fontSize: '1rem',
                  fontWeight: 700,
                  height: '40px',
                  fontVariantNumeric: 'tabular-nums',
                }}
              />
            </div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setManualMark(formattedDefaultMark)}
              style={{
                alignSelf: 'flex-end',
                fontSize: '0.76rem',
                height: '40px',
                padding: '0 0.75rem',
              }}
              title="Copiar tiempo transcurrido del bloque"
            >
              <Clock size={14} />
              <span>Usar tiempo bloque ({formattedDefaultMark})</span>
            </button>
          </div>
        </div>

        {/* Segmentos de grabación */}
        <div style={{ marginBottom: '1rem' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '0.5rem',
            }}
          >
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
              SEGMENTOS DE CÁMARA / GRABACIÓN ({segments.length})
            </span>
            <button
              type="button"
              onClick={() => setShowAddSegment(!showAddSegment)}
              style={{
                fontSize: '0.74rem',
                color: 'var(--brand-red)',
                background: 'none',
                border: 'none',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
              }}
            >
              <Plus size={14} />
              <span>Añadir corte de cámara</span>
            </button>
          </div>

          {/* Formulario nuevo segmento */}
          {showAddSegment && (
            <div
              style={{
                backgroundColor: 'var(--surface-subtle)',
                border: '1px dashed var(--surface-border-strong)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.85rem',
                marginBottom: '0.75rem',
              }}
            >
              <div style={{ marginBottom: '0.5rem' }}>
                <label className="form-label" style={{ fontSize: '0.72rem' }}>
                  Nombre del segmento / Toma *
                </label>
                <input
                  type="text"
                  placeholder="Ej. Cámara Lateral - Parte 1"
                  value={newSegName}
                  onChange={(e) => setNewSegName(e.target.value)}
                  className="form-input"
                  style={{ fontSize: '0.82rem' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <div>
                  <label className="form-label" style={{ fontSize: '0.72rem' }}>
                    Nombre del archivo
                  </label>
                  <input
                    type="text"
                    placeholder="DSC_0042.MP4"
                    value={newSegFileName}
                    onChange={(e) => setNewSegFileName(e.target.value)}
                    className="form-input"
                    style={{ fontSize: '0.82rem' }}
                  />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: '0.72rem' }}>
                    Offset estimado (seg)
                  </label>
                  <input
                    type="number"
                    placeholder="0"
                    value={newSegOffset}
                    onChange={(e) => setNewSegOffset(Number(e.target.value))}
                    className="form-input"
                    style={{ fontSize: '0.82rem' }}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '0.5rem' }}>
                <label className="form-label" style={{ fontSize: '0.72rem' }}>
                  Enlace a Drive / Nube (Opcional)
                </label>
                <input
                  type="url"
                  placeholder="https://drive.google.com/..."
                  value={newSegDriveUrl}
                  onChange={(e) => setNewSegDriveUrl(e.target.value)}
                  className="form-input"
                  style={{ fontSize: '0.82rem' }}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddSegment(false)}
                  style={{ fontSize: '0.76rem', padding: '0.3rem 0.6rem' }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddSegment}
                  disabled={!newSegName.trim()}
                  style={{
                    fontSize: '0.76rem',
                    padding: '0.3rem 0.6rem',
                    backgroundColor: 'var(--brand-red)',
                  }}
                >
                  Guardar segmento
                </button>
              </div>
            </div>
          )}

          {/* Lista de segmentos existentes */}
          {segments.length === 0 ? (
            <div
              style={{
                padding: '0.85rem',
                textAlign: 'center',
                backgroundColor: 'var(--surface-subtle)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.78rem',
                color: 'var(--text-muted)',
              }}
            >
              No hay segmentos creados todavía. Puedes añadir el archivo de vídeo o registrar la marca
              directamente.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {segments.map((seg) => {
                const isSelected = seg.id === selectedSegId
                return (
                  <div
                    key={seg.id}
                    onClick={() => setSelectedSegId(seg.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.55rem 0.75rem',
                      borderRadius: 'var(--radius-sm)',
                      border: isSelected ? '1px solid var(--brand-red)' : '1px solid var(--surface-border)',
                      backgroundColor: isSelected ? 'var(--brand-red-subtle)' : 'var(--surface-card)',
                      cursor: 'pointer',
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          color: isSelected ? 'var(--brand-red)' : 'var(--text-main)',
                        }}
                      >
                        {seg.name}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        {seg.file_name && `Archivo: ${seg.file_name} · `}
                        Offset: {seg.offset_seconds}s
                        {seg.drive_url && ' · Drive vinculado'}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {isSelected && <CheckCircle2 size={16} color="var(--brand-red)" />}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteSegment(seg.id)
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          padding: '0.2rem',
                        }}
                        title="Eliminar segmento"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Acciones de cierre */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '0.5rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid var(--surface-border)',
          }}
        >
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            style={{ fontSize: '0.82rem', minHeight: '44px' }}
          >
            Cerrar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleApplyMark}
            style={{
              fontSize: '0.82rem',
              minHeight: '44px',
              backgroundColor: 'var(--brand-red)',
            }}
          >
            Vincular marca al Snap #{currentSnapSequence}
          </button>
        </div>
      </div>
    </div>
  )
}
